import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";

export function Header() {
  const { t, lang } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang: routeLang } = useParams();

  // Compute path suffix (everything after /fr or /en)
  const suffix = location.pathname.replace(/^\/(fr|en)/, "") || "";

  const switchLang = (target) => {
    if (target === lang) return;
    const newPath = `/${target}${suffix}`;
    navigate(newPath);
  };

  const navItems = [
    { key: "home", to: `/${lang}`, end: true },
    { key: "expertise", to: `/${lang}#expertise` },
    { key: "insights", to: `/${lang}#insights` },
    { key: "legal", to: `/${lang}/terms` },
  ];

  const linkBase =
    "text-sm font-medium tracking-wide text-slate-200 hover:text-white px-1 py-2 border-b-2 border-transparent hover:border-amber-500";
  const linkActive = "text-white border-amber-500";

  return (
    <header
      className="bg-slate-900 text-white sticky top-0 z-40 border-b border-slate-800"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-6">
        <Link
          to={`/${lang}`}
          className="flex items-center gap-3"
          data-testid="header-logo"
          onClick={() => setOpen(false)}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-slate-900 font-black tracking-tighter">
            E
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-black tracking-[0.18em] text-[15px]">
              {t("brand.name")}
            </span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-slate-400">
              {t("brand.tagline")}
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : ""}`
              }
              data-testid={`nav-${item.key}`}
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2" data-testid="lang-switcher-desktop">
          <LangButton current={lang} target="fr" onClick={switchLang} />
          <span className="text-slate-600">/</span>
          <LangButton current={lang} target="en" onClick={switchLang} />
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-slate-700 text-white"
          aria-label={open ? t("nav.close_menu") : t("nav.open_menu")}
          aria-expanded={open}
          data-testid="mobile-menu-button"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-slate-900 border-t border-slate-800" data-testid="mobile-nav">
          <div className="px-5 sm:px-8 py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className="text-slate-200 py-2 border-b border-slate-800 text-base font-medium"
                data-testid={`nav-mobile-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <span className="text-xs uppercase tracking-widest text-slate-400">
                {t("nav.language")}
              </span>
              <LangButton current={lang} target="fr" onClick={(l) => { switchLang(l); setOpen(false); }} />
              <LangButton current={lang} target="en" onClick={(l) => { switchLang(l); setOpen(false); }} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function LangButton({ current, target, onClick }) {
  const active = current === target;
  return (
    <button
      type="button"
      onClick={() => onClick(target)}
      className={`px-2 py-1 text-xs font-semibold uppercase tracking-widest rounded ${
        active ? "text-amber-400" : "text-slate-300 hover:text-white"
      }`}
      data-testid={`lang-switcher-${target}`}
      aria-pressed={active}
    >
      {target}
    </button>
  );
}

export default Header;
